<template>
  <div class="assignment-list-wrapper">
    <div class="assignment-list" v-if="filteredAssignments.length > 0" @scroll="handleScroll" ref="assignmentList">
      <AssignmentCardWithDraftDate v-for="assignment in filteredAssignments" :key="assignment.id"
        :assignment="assignment" :selectedAssignment="selectedAssignmentLocal"
        :isFromEmail="assignment.id === emailExtractedAssignmentId" :showActiveLabel="true"
        :showCreatedDate="showCreatedDate" @select="handleAssignmentSelection(assignment)" />
    </div>

    <div v-if="effectiveIsLoadingMore" class="loading-more-container">
      <IconProvider name="Loader" class="loader animate-spin" />
      <div class="loading-text">Loading assignments...</div>
    </div>

    <div v-if="shouldShowEndMessage" class="end-of-results">
      <div class="loading-text">No more assignments to load</div>
    </div>

    <div v-if="!isLoading && !effectiveIsLoadingMore && filteredAssignments.length === 0" class="empty-state">
      <div class="loading-text">No assignments found</div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import AssignmentCardWithDraftDate from "./AssignmentCardWithDraftDate.vue";
import IconProvider from "../../commons/IconProvider.vue";
import DateRangePicker from "../../commons/DateRangePicker.vue";
import { searchAssignmentsFromEmail } from "../../../service/AssignmentSearchService.js";
import moment from "moment";

export default {
  name: "AssignmentList",
  components: { AssignmentCardWithDraftDate, IconProvider, DateRangePicker },
  props: {
    loadOnMount: {
      type: Boolean,
      default: true,
    },
    autoExtractFromEmail: {
      type: Boolean,
      default: true,
    },
    selectedAssignment: {
      type: Object,
      default: null,
    },
    showCreatedDate: {
      type: Boolean,
      default: false,
    },
    customAssignments: {
      type: Array,
      default: null,
    },
    externalLoadingMore: {
      type: Boolean,
      default: false,
    },
    externalHasMore: {
      type: Boolean,
      default: false,
    },
    dateRangeStart: {
      type: [String, Object],
      default: null,
    },
    dateRangeEnd: {
      type: [String, Object],
      default: null,
    },
    context: {
      type: String,
      default: "default",
      validator: (value) =>
        ["default", "clientFollowUp", "propertyFollowUp"].includes(value),
    },
  },
  data() {
    return {
      searchText: "",
      selectedAssignmentLocal: null,
      // Pagination state
      currentPage: 1,
      pageSize: 50,
      isLoadingMore: false,
      hasReachedEnd: false,
      searchDebounceTimer: null,
      emailExtractedAssignmentId: null,
      manualSelectionMade: false,
      startDate: "",
      endDate: "",
      showEndMessage: false,
      endMessageTimer: null,
    };
  },
  created() {
    if (this.loadOnMount) {
      this.initializeAssignments();
    }
    this.selectedAssignmentLocal = this.selectedAssignment;
  },
  computed: {
    ...mapGetters({
      isLoading: "assignments/isLoading",
      defaultAssignments: "assignments/getAssignments",
      clientFollowUpAssignments: "assignments/getClientFollowUpAssignments",
    }),
    assignments() {
      const result =
        this.context === "clientFollowUp"
          ? this.clientFollowUpAssignments
          : this.defaultAssignments;
      return result;
    },
    filteredAssignments() {
      // If custom assignments are provided, use them instead of store
      const sourceAssignments =
        this.customAssignments !== null ? this.customAssignments : this.assignments;
      let filtered = sourceAssignments;

      // Only filter out extracted assignment when using store assignments (not custom)
      // Custom assignments should not be filtered
      if (this.customAssignments === null && this.emailExtractedAssignmentId) {
        // Filter out the assignment that was extracted from email (smart search result)
        filtered = filtered.filter(
          (assignment) => assignment.id !== this.emailExtractedAssignmentId
        );
      }

      // For Property Follow Up context, filter out assignments with completed POD (Billing status)
      if (this.context === "propertyFollowUp") {
        filtered = filtered.filter((assignment) => {
          const statusId = assignment?.trackerEngagementStatus?.id;
          // Filter out Billing (5) and Completed (6) statuses
          return statusId !== 5 && statusId !== 6;
        });
      }

      return filtered;
    },
    effectiveIsLoadingMore() {
      return this.customAssignments !== null
        ? this.externalLoadingMore
        : this.isLoadingMore;
    },
    effectiveHasReachedEnd() {
      return this.customAssignments !== null ? !this.externalHasMore : this.hasReachedEnd;
    },
    shouldShowEndMessage() {
      // Base condition: must have reached end, not loading, and have assignments
      const baseCondition =
        this.effectiveHasReachedEnd &&
        !this.effectiveIsLoadingMore &&
        this.filteredAssignments.length > 0;

      if (!baseCondition) {
        return false;
      }

      // For propertyFollowUp context, only show if:
      // 1. There are more than 5 assignments
      // 2. User scrolled down (showEndMessage is true)
      if (this.context === "propertyFollowUp") {
        return this.filteredAssignments.length > 5 && this.showEndMessage;
      }

      // For other contexts, show normally
      return true;
    },
  },
  watch: {
    searchText() {
      this.debouncedSearch();
    },
    selectedAssignment(newVal) {
      if (!this.manualSelectionMade) {
        this.selectedAssignmentLocal = newVal;
      }
    },
    assignments: {
      immediate: true,
      handler(newAssignments) {
        if (newAssignments && newAssignments.length > 0) {
          this.updateDateRangeFromAssignments();
        }
      },
    },
    dateRangeStart() {
     this.refreshAssignmentsWithDateRange();
    },
    dateRangeEnd() {
      this.refreshAssignmentsWithDateRange();
    },
  },
  methods: {
    ...mapActions({
      fetchAssignments: "assignments/fetchAssignments",
      fetchAssignmentsClientFollowUp: "assignments/fetchAssignmentsClientFollowUp",
      fetchAssignmentChecklist: "assignments/fetchAssignmentChecklist",
    }),

    async extractAndPrioritizeFromEmail() {
      if (!this.autoExtractFromEmail) return null;

      try {

        const { getCurrentEmailData } = await import(
          "../../../utils/emailAssignmentExtractor"
        );
        const emailData = await getCurrentEmailData();

        if (!emailData) {
          console.warn("No email data available for search");
          return null;
        }


        const searchResult = await searchAssignmentsFromEmail(emailData, {
          maxResults: 20,
          includeScoring: true,
          fallbackToFuzzy: true,
        });

        if (searchResult.assignments && searchResult.assignments.length > 0) {
          const topAssignment = searchResult.assignments[0];

          this.emailExtractedAssignmentId = topAssignment.id;

          const mergedAssignments = this.mergeAndPrioritizeAssignments(
            searchResult.assignments,
            this.assignments || []
          );
          this.$store.commit("assignments/SET_ASSIGNMENTS", mergedAssignments);

          const strategyNames = {
            FILE_ID: "File ID match",
            ASSIGNMENT_ID: "Assignment ID match",
            PROPERTY_NAME: "Property name match",
            PROPERTY_ADDRESS: "Address match",
            CLIENT_DETAILS: "Client details match",
          };

          this.$emit("showNotification", {
            type: "success",
            message: `Found assignment ${topAssignment.code} via ${strategyNames[searchResult.searchStrategy] || searchResult.searchStrategy
              }`,
          });

          return topAssignment;
        } else {
          console.log("❌ No assignment found via hierarchical search");
          return null;
        }
      } catch (error) {
        console.error("❌ Error in hierarchical search from email:", error);
        this.$emit("showNotification", {
          type: "error",
          message: "Error searching for assignment from email content",
        });
        return null;
      }
    },

    debouncedSearch() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      this.searchDebounceTimer = setTimeout(() => {
        this.$store.dispatch("assignments/resetAssignments");
        this.initializeAssignments();
      }, 500);
    },

    async initializeAssignments() {
      try {
        console.log("🔄 Initializing assignments...");
        this.currentPage = 1;
        this.hasReachedEnd = false;
        this.isLoadingMore = true;

        if (this.context === "clientFollowUp") {
          const startDate = this.dateRangeStart
            ? moment(this.dateRangeStart).format("YYYY-MM-DD")
            : null;
          const endDate = this.dateRangeEnd
            ? moment(this.dateRangeEnd).format("YYYY-MM-DD")
            : null;

          await this.fetchAssignmentsClientFollowUp({
            draftDeliveredDateStartDate: startDate,
            draftDeliveredDateEndDate: endDate,
            reset: true,
          });
        } else {
          await this.fetchAssignments({
            page: this.currentPage,
            pageSize: this.pageSize,
            searchText: this.searchText?.trim(),
            reset: true,
          });
        }
        this.isLoadingMore = false;
      } catch (error) {
        console.error("❌ Error initializing assignments:", error);
        this.isLoadingMore = false;
      }
    },

    async handleScroll(event) {
      const element = event.target;
      const threshold = 50;

      if (element.scrollHeight - element.scrollTop <= element.clientHeight + threshold) {
        // Trigger end message display for propertyFollowUp context
        if (
          this.context === "propertyFollowUp" &&
          this.effectiveHasReachedEnd &&
          this.filteredAssignments.length > 5
        ) {
          this.showEndMessageWithTimeout();
        }

        // If using custom assignments, emit event instead of loading directly
        if (this.customAssignments !== null) {
          this.$emit("loadMore");
        } else {
          await this.loadMore();
        }
      }
    },

    async loadMore() {
      if (this.isLoadingMore || this.hasReachedEnd || this.isLoading) {
        return;
      }

      try {
        this.isLoadingMore = true;
        this.currentPage += 1;

        let result = await this.fetchAssignments({
          page: this.currentPage,
          pageSize: this.pageSize,
          searchText: this.searchText?.trim(),
          reset: false,
        });

        if (!result || result.length < this.pageSize) {
          this.hasReachedEnd = true;
        }
      } catch (error) {
        console.error("Error loading more assignments:", error);
        this.currentPage -= 1;
      } finally {
        this.isLoadingMore = false;
      }
    },

    async handleAssignmentSelection(assignment) {
      this.manualSelectionMade = true;
      this.selectedAssignmentLocal = assignment;

      this.$emit("select", assignment);

      this.fetchAssignmentChecklist(assignment?.viewEventId)
        .then((checklist) => {
          console.log("📋 Fetched Checklist:", checklist);
        })
        .catch((error) => {
          console.warn("⚠️ Failed to fetch assignment checklist:", error);
        });
    },

    async refreshAssignments() {
      this.currentPage = 1;
      this.hasReachedEnd = false;
      await this.initializeAssignments();
      this.$emit("refresh");
    },

    clearSelection() {
      this.selectedAssignmentLocal = null;
      this.manualSelectionMade = false;
      this.$emit("select", null);
    },

    mergeAndPrioritizeAssignments(searchResults, currentAssignments) {
      const existingMap = new Map();
      currentAssignments.forEach((assignment) => {
        existingMap.set(assignment.id, assignment);
      });

      const mergedAssignments = [];
      const addedIds = new Set();

      searchResults.forEach((searchResult) => {
        const existing = existingMap.get(searchResult.id);
        mergedAssignments.push({
          ...existing,
          ...searchResult,
          searchScore: searchResult.searchScore,
          searchStrategy: searchResult.searchStrategy,
        });
        addedIds.add(searchResult.id);
      });

      currentAssignments.forEach((assignment) => {
        if (!addedIds.has(assignment.id)) {
          mergedAssignments.push(assignment);
        }
      });

      return mergedAssignments;
    },

    updateDateRangeFromAssignments() {
      if (!this.assignments || this.assignments.length === 0) {
        this.startDate = "";
        this.endDate = "";
        return;
      }

      const validDates = this.assignments
        .map((a) => a.createdTimestamp || a.createdDate || a.dateCreated || a.created)
        .filter((date) => date && moment(date).isValid())
        .map((date) => moment(date));

      if (validDates.length === 0) {
        return;
      }

      const sortedDates = validDates.sort((a, b) => a - b);
      const startMoment = sortedDates[0];
      const endMoment = sortedDates[sortedDates.length - 1];

      this.startDate = startMoment.format("MM/DD/YYYY");
      this.endDate = endMoment.format("MM/DD/YYYY");
    },

    onDateRangeChange({ startDate, endDate }) {
      this.startDate = startDate;
      this.endDate = endDate;
      this.$emit("dateRangeChanged", { startDate, endDate });
    },
    async refreshAssignmentsWithDateRange() {
      if (!this.dateRangeStart || !this.dateRangeEnd || this.dateRangeStart?.length !== 10 || this.dateRangeEnd?.length !== 10) {
        return;
      }
      await this.initializeAssignments();
    },
    showEndMessageWithTimeout() {
      // Clear any existing timer
      if (this.endMessageTimer) {
        clearTimeout(this.endMessageTimer);
      }

      // Show the message
      this.showEndMessage = true;

      // Hide after 5 seconds
      this.endMessageTimer = setTimeout(() => {
        this.showEndMessage = false;
        this.endMessageTimer = null;
      }, 5000);
    },
  },
  beforeUnmount() {
    // Cleanup timer on component unmount
    if (this.endMessageTimer) {
      clearTimeout(this.endMessageTimer);
    }
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  },
};
</script>

<style scoped>
.assignment-list-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

.search-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 8px;
  margin-bottom: 8px;
}

.search-input {
  flex: 1;
}

.date-range-wrapper {
  margin-top: 12px;
  margin-bottom: 20px;
}

.assignment-list {
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 400px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  scroll-behavior: smooth;
  flex: 1;
  background-color: #f5f5f5;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
}

.loading-more-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 16px;
}

.end-of-results {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  color: #666;
  font-style: italic;
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px;
  color: #666;
}

.loading-text {
  font-size: 15px;
  color: #666;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.checkbox-description {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  margin: 0;
  padding: 0;
  margin-left: 0;
  /* Align description to start at the same position as checkbox */
  text-align: right;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  color: #000;
  font-size: 16px;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

.loader {
  width: 24px;
  height: 24px;
}
</style>
